import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { NumberField } from './NumberField';
import { SupplierPicker } from './SupplierPicker';
import {
  SkuGroupPicker, OrderCategoryPicker, UomPicker, QtyField,
  FormSection, FieldHint, RequiredMark,
} from './TechsFields';

/**
 * <TechsOrderSection> — 「TECHS発注に必要な情報」のかたまり（2026-06）。
 *
 * TECHS がいまの発注の本体なので、**どの台帳の登録フォームでも同じ項目・同じ並び・
 * 同じ名前・同じ説明**でここに集める。ここが埋まっていれば 部品受注入力 →
 * TECHS 部品受注入力 / 部品手配入力 まで打ち直しなしで進む。
 * 参照の正は 技術資産（F1）の必須項目。
 *
 * 1本の部品なので、共通ブロック（`CommonRegistrationBlock`）も、既存の作りを
 * 保ったままの F1/F4/F6 も、同じこれを描く（2か所に書かない）。
 */
export const TechsOrderSection = ({
  value, onChange, editing = false, fields, requiredKeys = [],
  register, appCode = '', currentNo = '', testPrefix = 'techs',
  labels = {}, onNumberIssued = null, deptCode = '',
  title = 'TECHS発注に必要な情報',
  note = 'ここが埋まっていれば、部品受注入力からTECHSの部品受注入力・部品手配入力まで打ち直しなしで進みます。',
}) => {
  const has = (k) => fields.includes(k);
  const req = (k) => requiredKeys.includes(k);
  const patch = (p) => onChange({ ...value, ...p });
  const set = (k) => (e) => patch({ [k]: e?.target ? e.target.value : e });
  const mark = (k) => <RequiredMark show={req(k) && !editing} />;
  const L = {
    number: '品番 / 資産番号', sku_group: '品番グループ', order_category: '受注分類',
    model_no: '型式・仕様', maker_pn: 'メーカー品番', uom: '単位', qty: '数量',
    supplier: '仕入先（注文先）', reference_url: '注文URL', ...labels,
  };

  return (
    <FormSection tone="techs" title={title} note={note}
      testId={`${testPrefix}-section-techs`}>
      {has('number') && (
        <NumberField label={L.number} register={register} appCode={appCode}
          editing={editing} currentNo={currentNo} required={req('number')}
          value={value?.number_value} onChange={(v) => patch({ number_value: v })}
          onIssued={onNumberIssued} deptCode={deptCode}
          testId={`${testPrefix}-number`} />
      )}

      <div className="grid grid-cols-2 gap-3">
        {has('sku_group') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.sku_group} {mark('sku_group')}</Label>
            <SkuGroupPicker value={value?.sku_group_id || ''}
              onChange={(v) => patch({ sku_group_id: v })} testId={`${testPrefix}-sku-group`} />
            <FieldHint>品番の系列。TECHSで品番を探すときの手がかりになります</FieldHint>
          </div>
        )}
        {has('order_category') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.order_category} {mark('order_category')}</Label>
            <div className="mt-1">
              <OrderCategoryPicker value={value?.order_category_id || null}
                onChange={(next) => patch(next)} testId={`${testPrefix}-order-category`} />
            </div>
            <FieldHint>TECHSの受注区分。発注の振り分けと集計に使われます</FieldHint>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {has('model_no') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.model_no} {mark('model_no')}</Label>
            <Input value={value?.model_no || ''} onChange={set('model_no')}
              className="h-10 mt-1 font-mono" data-testid={`${testPrefix}-model`} />
            <FieldHint>TECHSの発注明細にそのまま載ります。仕入先が品物を特定する決め手です</FieldHint>
          </div>
        )}
        {has('maker_pn') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.maker_pn} {mark('maker_pn')}</Label>
            <Input value={value?.maker_pn || ''} onChange={set('maker_pn')}
              className="h-10 mt-1 font-mono" data-testid={`${testPrefix}-maker-pn`} />
            <FieldHint>分かる場合は入れてください。仕入先への型番照会が一度で済みます</FieldHint>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {has('uom') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.uom} {mark('uom')}</Label>
            <UomPicker value={value?.uom || ''} onChange={(v) => patch({ uom: v })}
              testId={`${testPrefix}-uom`} />
            <FieldHint>TECHSの発注単位。個・本・mなど、注文する単位で入れます</FieldHint>
          </div>
        )}
        {has('qty') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.qty} {mark('qty')}</Label>
            <QtyField value={value?.qty} onChange={(v) => patch({ qty: v })}
              testId={`${testPrefix}-qty`} />
            <FieldHint>いま手元にある数。発注数はこの数を見て決めます</FieldHint>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {has('supplier') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.supplier} {mark('supplier')}</Label>
            <div className="mt-1">
              <SupplierPicker value={value} onChange={(next) => patch(next)}
                testId={`${testPrefix}-supplier`} />
            </div>
            <FieldHint>TECHSの発注先。分かる場合は入れておくと発注時に選び直す手間が減ります</FieldHint>
          </div>
        )}
        {has('reference_url') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{L.reference_url}</Label>
            <Input value={value?.reference_url || ''} onChange={set('reference_url')}
              placeholder="https://…" className="h-10 mt-1" data-testid={`${testPrefix}-url`} />
            <FieldHint>通販の商品ページ。次に買う人が同じ物を迷わず選べます</FieldHint>
          </div>
        )}
      </div>
    </FormSection>
  );
};

export default TechsOrderSection;
